import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Plus, TrendingUp, CalendarDays, LayoutGrid, List, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import VenueTabs from "@/pages/dashboard/VenueTabs";
import BookDateModal from "@/pages/dashboard/BookDateModal";
import BusinessLogo from "@/pages/dashboard/BusinessLogo";
import { Skeleton } from "@/components/ui/skeleton";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const compactINR = (n) => {
  if (n >= 1000) return `₹${parseFloat((n / 1000).toFixed(1)).toString()}k`;
  return `₹${n}`;
};
const pendingDue = (b) => (b?.pending_amount > 0 && !b?.fully_paid ? b.pending_amount : 0);

export default function CalendarView() {
  const { businessId, venues, venuesLoading, selectedVenue, setSelectedVenue, monthDate, setMonthDate, user, can } = useDash();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState("");
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("month");
  const [settleTarget, setSettleTarget] = useState(null);
  const canSettle = can?.payments !== false;

  const settleBalance = async (mode) => {
    try {
      await api.post(`/bookings/${settleTarget.booking_id}/settle`, { mode }, { params: businessId ? { business_id: businessId } : {} });
      toast.success("Balance settled — marked fully paid");
      setSettleTarget(null);
      refreshAll();
      qc.invalidateQueries({ queryKey: ["pending-payments"] });
      qc.invalidateQueries({ queryKey: ["payments-summary"] });
    } catch (e) { toast.error("Could not update"); }
  };

  useEffect(() => {
    if (!venues.length) return;
    if (!selectedVenue || !venues.some((v) => v.venue_id === selectedVenue.venue_id)) setSelectedVenue(venues[0]);
  }, [venues, selectedVenue, setSelectedVenue]);

  const activeVenue = selectedVenue || venues[0];
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["bookings", businessId, activeVenue?.venue_id, year, month],
    queryFn: async () => (await api.get("/bookings", { params: { business_id: businessId, venue_id: activeVenue?.venue_id, month: month + 1, year } })).data,
    enabled: !!businessId && !!activeVenue,
  });

  const { data: revenue } = useQuery({
    queryKey: ["revenue", businessId],
    queryFn: async () => (await api.get("/revenue/summary", { params: { business_id: businessId } })).data,
    enabled: !!businessId && !!can?.revenue,
  });

  const { data: access } = useQuery({
    queryKey: ["access", businessId],
    queryFn: async () => (await api.get("/access", { params: businessId ? { business_id: businessId } : {} })).data,
  });

  const byDay = useMemo(() => {
    const map = {};
    bookings.forEach((b) => { (map[b.date] = map[b.date] || []).push(b); });
    return map;
  }, [bookings]);

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["bookings"] });
    qc.invalidateQueries({ queryKey: ["revenue"] });
    qc.invalidateQueries({ queryKey: ["venues"] });
    qc.invalidateQueries({ queryKey: ["analytics"] });
  };

  const grid = useMemo(() => {
    const first = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    return cells;
  }, [year, month]);

  const today = new Date();
  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const isPast = (d) => new Date(year, month, d) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const openNew = (d) => { setEditing(null); setModalDate(iso(year, month, d || today.getDate())); setModalOpen(true); };
  const openEdit = (b) => { setEditing(b); setModalDate(b.date); setModalOpen(true); };
  const changeMonth = (dir) => setMonthDate(new Date(year, month + dir, 1));

  const monthBookings = useMemo(() => {
    const agg = {};
    bookings.forEach((b) => {
      const k = b.booker_name || "Unknown";
      agg[k] = agg[k] || { name: k, count: 0, revenue: 0 };
      agg[k].count++; agg[k].revenue += b.amount || 0;
    });
    return Object.values(agg).sort((a, b) => b.revenue - a.revenue);
  }, [bookings]);

  const sortedBookings = useMemo(() => [...bookings].sort((a, b) => a.date.localeCompare(b.date)), [bookings]);

  if (venuesLoading) return <Skeleton className="h-96 w-full rounded-xl" />;

  return (
    <div className="space-y-6">
      <BusinessLogo />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Calendar</h1>
          <p className="text-sm text-slate">Book and manage dates across your venues.</p>
        </div>
        <button onClick={() => openNew(null)} data-testid="book-date-btn"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-pulse-green px-5 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.97] sm:w-auto sm:py-2.5">
          <Plus size={16} /> Book Date
        </button>
      </div>

      {/* Revenue summary */}
      {can?.revenue && (
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-pulse-sm" data-testid="revenue-month-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-slate">Revenue this month</span>
            <TrendingUp size={18} className="text-pulse-green" />
          </div>
          <div className="mt-2 text-3xl font-bold text-pulse-navy">{formatINR(revenue?.month_revenue)}</div>
          <div className="mt-1 text-sm text-slate">{revenue?.month_bookings || 0} bookings</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-pulse-navy p-6 text-white shadow-pulse-sm grain-overlay" data-testid="revenue-fy-card">
          <div className="relative z-10 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-white/60">{revenue?.fy_label || "FY"} to date</span>
            <CalendarDays size={18} className="text-pulse-green" />
          </div>
          <div className="relative z-10 mt-2 text-3xl font-bold">{formatINR(revenue?.fy_revenue)}</div>
          <div className="relative z-10 mt-1 text-sm text-white/60">{revenue?.fy_bookings || 0} bookings this financial year</div>
        </div>
      </div>
      )}

      <VenueTabs />

      {/* Calendar grid */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm" data-testid="calendar-grid">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-pulse-navy">{MONTHS[month]} {year}</h2>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5 rounded-md border border-slate-200 p-0.5" data-testid="calendar-view-toggle">
              <button onClick={() => setView("month")} data-testid="view-month" className={`grid h-8 w-8 place-items-center rounded transition-colors ${view === "month" ? "bg-pulse-navy text-white" : "text-slate hover:bg-cloud"}`}><LayoutGrid size={16} /></button>
              <button onClick={() => setView("agenda")} data-testid="view-agenda" className={`grid h-8 w-8 place-items-center rounded transition-colors ${view === "agenda" ? "bg-pulse-navy text-white" : "text-slate hover:bg-cloud"}`}><List size={16} /></button>
            </div>
            <div className="flex gap-1">
              <button onClick={() => changeMonth(-1)} data-testid="cal-prev-month" className="grid h-8 w-8 place-items-center rounded-md border border-slate-200 text-slate transition-colors hover:bg-cloud"><ChevronLeft size={18} /></button>
              <button onClick={() => setMonthDate(new Date(today.getFullYear(), today.getMonth(), 1))} className="rounded-md border border-slate-200 px-3 text-sm font-medium text-slate transition-colors hover:bg-cloud">Today</button>
              <button onClick={() => changeMonth(1)} data-testid="cal-next-month" className="grid h-8 w-8 place-items-center rounded-md border border-slate-200 text-slate transition-colors hover:bg-cloud"><ChevronRight size={18} /></button>
            </div>
          </div>
        </div>

        {view === "month" ? (
        <div className="mt-4 grid grid-cols-7 gap-1.5">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
            <div key={d} className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wide text-slate">{d.slice(0,1)}<span className="hidden sm:inline">{d.slice(1)}</span></div>
          ))}
          {grid.map((d, i) => {
            if (!d) return <div key={`empty-${i}`} />;
            const dateStr = iso(year, month, d);
            const dayBookings = byDay[dateStr] || [];
            const past = isPast(d);
            return (
              <button key={dateStr} onClick={() => openNew(d)} data-testid={`cal-day-${d}`}
                className={`group relative min-h-[76px] rounded-md border p-1.5 text-left transition-colors sm:min-h-[96px] ${
                  isToday(d) ? "border-pulse-green bg-pulse-green/5" : "border-slate-100 hover:bg-cloud"
                }`}>
                <span className={`text-xs font-semibold ${past && !isToday(d) ? "text-slate/60" : isToday(d) ? "text-pulse-green" : "text-charcoal"}`}>{d}</span>
                <div className="mt-1 space-y-1">
                  {dayBookings.slice(0, 3).map((b) => (
                    <div key={b.booking_id} onClick={(e) => { e.stopPropagation(); openEdit(b); }}
                      data-testid={`booking-chip-${b.booking_id}`}
                      className="rounded px-1.5 py-0.5 text-[10px] font-medium text-white sm:text-[11px]"
                      style={{ background: activeVenue?.color, opacity: b.status === "tentative" ? 0.5 : 1 }}>
                      <span className="block truncate">{b.booker_name}{b.status === "tentative" ? " ·?" : ""}</span>
                      {pendingDue(b) > 0 && (
                        <span onClick={(e) => { e.stopPropagation(); canSettle ? setSettleTarget(b) : openEdit(b); }}
                          data-testid={`chip-pending-${b.booking_id}`}
                          className="mt-0.5 inline-block rounded-sm bg-red-600 px-1 text-[9px] font-semibold leading-tight text-white sm:text-[10px]">
                          {compactINR(pendingDue(b))} due
                        </span>
                      )}
                    </div>
                  ))}
                  {dayBookings.length > 3 && <div className="text-[10px] text-slate">+{dayBookings.length - 3} more</div>}
                </div>
              </button>
            );
          })}
        </div>
        ) : (
          <div className="mt-4 space-y-2" data-testid="agenda-list">
            {sortedBookings.length === 0 ? (
              <div className="rounded-md bg-cloud py-10 text-center">
                <CalendarDays className="mx-auto text-slate/50" size={28} />
                <p className="mt-2 text-sm font-medium text-slate">No bookings in {MONTHS[month]}</p>
                <p className="text-xs text-slate/70">Tap "Book Date" to add one.</p>
              </div>
            ) : sortedBookings.map((b) => (
              <button key={b.booking_id} onClick={() => openEdit(b)} data-testid={`agenda-${b.booking_id}`}
                className="flex w-full items-center justify-between rounded-md border border-slate-100 p-3 text-left transition-colors hover:bg-cloud">
                <div className="flex items-center gap-3">
                  <div className="flex w-11 shrink-0 flex-col items-center rounded-md py-1 text-white" style={{ background: activeVenue?.color, opacity: b.status === "tentative" ? 0.6 : 1 }}>
                    <span className="text-base font-bold leading-none">{new Date(b.date).getDate()}</span>
                    <span className="mt-0.5 text-[10px] uppercase">{new Date(b.date).toLocaleDateString("en-IN", { weekday: "short" })}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-charcoal">{b.booker_name}{b.status === "tentative" ? " · Tentative" : ""}</div>
                    <div className="truncate text-xs text-slate">{b.client_name || activeVenue?.name}</div>
                    {pendingDue(b) > 0 && (
                      <span onClick={(e) => { e.stopPropagation(); canSettle ? setSettleTarget(b) : openEdit(b); }}
                        data-testid={`agenda-pending-${b.booking_id}`}
                        className="mt-1 inline-block rounded-sm bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                        {compactINR(pendingDue(b))} due
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-sm font-semibold text-pulse-navy">{formatINR(b.amount)}</div>
              </button>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center gap-4 text-xs text-slate">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: activeVenue?.color }} /> Confirmed</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full opacity-50" style={{ background: activeVenue?.color }} /> Tentative</span>
        </div>
      </div>

      {/* Bookings this month for venue */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-pulse-sm">
        <h2 className="text-lg font-semibold text-pulse-navy">Bookings this month · {activeVenue?.name}</h2>
        {isLoading ? <Skeleton className="mt-4 h-20 w-full" /> : monthBookings.length === 0 ? (
          <div className="mt-6 rounded-md bg-cloud py-10 text-center">
            <CalendarDays className="mx-auto text-slate/50" size={32} />
            <p className="mt-3 text-sm font-medium text-slate">No bookings yet this month</p>
            <p className="text-xs text-slate/70">Tap a date on the calendar to add one.</p>
          </div>
        ) : (
          <div className="mt-4 divide-y divide-slate-100">
            {monthBookings.map((b) => (
              <div key={b.name} className="flex items-center justify-between py-3" data-testid={`month-booker-${b.name}`}>
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 place-items-center rounded-full text-sm font-semibold text-white" style={{ background: activeVenue?.color }}>{b.name[0]?.toUpperCase()}</div>
                  <div>
                    <div className="text-sm font-medium text-charcoal">{b.name}</div>
                    <div className="text-xs text-slate">{b.count} booking{b.count > 1 ? "s" : ""}</div>
                  </div>
                </div>
                <div className="text-sm font-semibold text-pulse-navy">{can?.revenue ? formatINR(b.revenue) : `${b.count} booking${b.count > 1 ? "s" : ""}`}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BookDateModal open={modalOpen} onClose={() => setModalOpen(false)} venues={venues} businessId={businessId}
        defaultVenueId={activeVenue?.venue_id} defaultDate={modalDate} editing={editing}
        currentUserName={user?.name} onSaved={refreshAll} />

      {settleTarget && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-pulse-navy/60 p-4 backdrop-blur-sm" data-testid="cal-settle-modal"
          onClick={() => setSettleTarget(null)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-pulse-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-pulse-navy">Settle balance</h3>
            <p className="mt-1 text-sm text-slate">{settleTarget.booker_name}{settleTarget.client_name ? ` · ${settleTarget.client_name}` : ""}</p>
            <div className="mt-4 rounded-md bg-amber-50 p-3">
              <div className="text-xs text-amber-800">Pending balance</div>
              <div className="text-2xl font-bold text-amber-900" data-testid="cal-settle-amount">{formatINR(pendingDue(settleTarget))}</div>
            </div>
            <p className="mt-4 text-xs font-medium text-slate">Balance received via:</p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <button onClick={() => settleBalance("online")} data-testid="cal-settle-online"
                className="flex items-center justify-center gap-1.5 rounded-md bg-pulse-green px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.98]"><Check size={15} /> Online</button>
              <button onClick={() => settleBalance("cash")} data-testid="cal-settle-cash"
                className="flex items-center justify-center gap-1.5 rounded-md bg-pulse-navy px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.98]"><Check size={15} /> Cash</button>
            </div>
            <button onClick={() => setSettleTarget(null)} data-testid="cal-settle-cancel" className="mt-3 w-full text-center text-xs text-slate">Cancel</button>
          </div>
        </div>
      )}

      {access?.state === "expired" && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-pulse-navy/70 p-4 backdrop-blur-sm" data-testid="trial-expired-popup">
          <div className="w-full max-w-md rounded-xl bg-white p-7 text-center shadow-pulse-lg">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-pulse-navy"><Lock className="text-pulse-green" size={26} /></div>
            <h3 className="mt-4 text-2xl font-bold text-pulse-navy">Your free trial has ended</h3>
            <p className="mt-2 text-sm text-slate">Continue running your bookings on Pulse — choose a plan:</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-md border border-slate-200 p-4"><div className="text-xs font-medium uppercase text-slate">Monthly</div><div className="mt-1 text-2xl font-bold text-pulse-navy">₹1,000</div><div className="text-xs text-slate">/ month</div></div>
              <div className="rounded-md border-2 border-pulse-green p-4"><div className="text-xs font-medium uppercase text-pulse-green">Yearly</div><div className="mt-1 text-2xl font-bold text-pulse-navy">₹840</div><div className="text-xs text-slate">/mo · ₹10,080/yr</div></div>
            </div>
            <a href="https://wa.me/918839790836" target="_blank" rel="noopener noreferrer" data-testid="unlock-whatsapp"
              className="mt-5 inline-block w-full rounded-md bg-pulse-green px-5 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]">
              Unlock on WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
